import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Sparkles, Trophy, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import MiniRouletteLogo from './MiniRouletteLogo';
import { trackEvent } from '../../lib/analytics';
import '../../styles/pirate-game-lobby-v3.css';

const R = 180;
const FALLBACK_COLORS = ['#E11D48', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#FCD34D'];

function pathFor(startDeg, endDeg) {
  const start = (startDeg - 90) * (Math.PI / 180);
  const end = (endDeg - 90) * (Math.PI / 180);
  const x1 = R * Math.cos(start);
  const y1 = R * Math.sin(start);
  const x2 = R * Math.cos(end);
  const y2 = R * Math.sin(end);
  return `M 0 0 L ${x1.toFixed(3)} ${y1.toFixed(3)} A ${R} ${R} 0 ${endDeg - startDeg > 180 ? 1 : 0} 1 ${x2.toFixed(3)} ${y2.toFixed(3)} Z`;
}

function splitLabel(label) {
  const parts = String(label || '').split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return [label || '', ''];
  if (parts.length === 2) return [parts[0], parts[1]];
  return [parts.slice(0, 2).join(' '), parts.slice(2).join(' ')];
}

function labelFontSize(label, sweep) {
  const length = String(label || '').length;
  if (sweep < 12) return 7;
  if (sweep < 18) return 8;
  if (length > 18) return 8;
  if (length > 12) return 9;
  return 11;
}

export default function RoulettePage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const [segments, setSegments] = useState([]);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const lockYRef = useRef(0);

  useEffect(() => {
    let live = true;
    Promise.all([
      api.get('/api/roulette/segments'),
      api.get('/api/roulette/my-spin-today').catch(() => ({ data: { spin: null } })),
    ])
      .then(([segmentsRes, todayRes]) => {
        if (!live) return;
        setSegments((segmentsRes.data?.segments || []).filter(item => item.is_active));
        setToday(todayRes.data?.spin || null);
      })
      .catch(() => toast.error('Could not load roulette'))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!spinning) return undefined;
    const body = document.body;
    const y = window.scrollY;
    lockYRef.current = y;
    const previous = { position: body.style.position, top: body.style.top, width: body.style.width };
    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.width = '100%';
    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.width = previous.width;
      window.scrollTo(0, lockYRef.current);
    };
  }, [spinning]);

  const wedges = useMemo(() => {
    if (!segments.length) return [];
    // Prize probability belongs to the draw logic, not to the wheel geometry.
    // Every visible section must occupy exactly the same angle so labels, studs,
    // pointer alignment and the final landing position all share one grid.
    const sweep = 360 / segments.length;
    return segments.map((seg, index) => {
      const start = index * sweep;
      const end = start + sweep;
      return {
        ...seg,
        index,
        sweep,
        center: start + sweep / 2,
        path: pathFor(start, Math.min(end, 359.999)),
        color: seg.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
      };
    });
  }, [segments]);

  const spin = async () => {
    if (spinning || !wedges.length) return;
    setResult(null);
    setSpinning(true);
    trackEvent('roulette_spin_start');
    try {
      const res = await api.post('/api/roulette/spin');
      const data = res.data || {};
      const targetIndex = Math.max(0, Math.min(wedges.length - 1, Number(data.segment_index || 0)));
      const targetCenter = wedges[targetIndex].center;
      const desired = (360 - targetCenter + 360) % 360;
      const current = ((rotation % 360) + 360) % 360;
      const nextRotation = rotation + ((desired - current + 360) % 360) + (360 * (5 + Math.floor(Math.random() * 3)));
      setRotation(nextRotation);
      setTimeout(() => {
        setSpinning(false);
        setResult(data);
        setToday(data.spin || null);
        if (Number(data.reward_fp || 0) > 0) toast.success(`+${data.reward_fp} FP credited`);
        trackEvent('roulette_spin_complete', { reward_fp: Number(data.reward_fp || 0) });
      }, 5100);
    } catch (err) {
      setSpinning(false);
      toast.error(err.response?.data?.detail || 'Spin failed');
    }
  };

  return (
    <div className="player-app vl-roulette-v3 min-h-screen pb-24 text-white" data-testid="roulette-page">
      <div className="vl-roulette-v3__world" aria-hidden="true"><span /><i /></div>
      <div className="vl-roulette-v3__header sticky top-0 z-30 px-4 py-3 flex items-center gap-3 backdrop-blur">
        <button
          onClick={() => navigate(homePath, { replace: true })}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-white/10 border border-white/10 active:scale-95 shrink-0"
          aria-label="Back"
          data-testid="roulette-back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <MiniRouletteLogo size={36} spin />
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-extrabold leading-tight">Captain's Fortune</h1>
          <p className="text-[11px] sm:text-xs text-gray-400 leading-4">Turn the helm. Claim the treasure.</p>
        </div>
      </div>

      <div className="vl-roulette-v3__deck max-w-4xl mx-auto px-4 sm:px-5 py-5 sm:py-8 flex flex-col items-center">
        {loading ? (
          <div className="py-20"><div className="spinner" /></div>
        ) : !wedges.length ? (
          <div className="text-center py-16 max-w-sm">
            <Sparkles className="w-10 h-10 mx-auto text-[#D4AF37]" />
            <h2 className="text-lg font-bold mt-3">No roulette prizes yet</h2>
            <p className="text-sm text-gray-400 mt-1">Ask an admin to seed or create active roulette segments.</p>
          </div>
        ) : (
          <>
            <div
              className="vl-roulette-v3__wheel-wrap relative mb-6"
              style={{ width: 'min(86vw, 390px)', maxWidth: '390px', aspectRatio: '1 / 1', touchAction: spinning ? 'none' : 'auto' }}
              data-testid="roulette-wheel-container"
            >
              <div className="vl-roulette-v3__aura absolute inset-0 rounded-full" />
              <div className="vl-roulette-v3__helm" aria-hidden="true">
                {Array.from({ length: 8 }, (_, index) => <span key={index} style={{ '--helm-index': index }} />)}
              </div>
              <div
                className="absolute inset-0 will-change-transform"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transition: spinning ? 'transform 5s cubic-bezier(.17,.67,.16,1)' : 'none',
                  transformOrigin: '50% 50%',
                  filter: 'drop-shadow(0 20px 35px rgba(0,0,0,.65))',
                }}
                data-testid="roulette-wheel"
              >
                <svg viewBox="-205 -205 410 410" className="w-full h-full block">
                  <circle r={R + 10} fill="#1b0f06" stroke="#d7972b" strokeWidth="10" />
                  <circle r={R + 2} fill="none" stroke="#5f3411" strokeWidth="8" />
                  {wedges.map((w) => {
                    const a = (w.center - w.sweep / 2 - 90) * (Math.PI / 180);
                    return (
                      <circle
                        key={`stud-${w.id || w.index}`}
                        cx={(R + 9) * Math.cos(a)}
                        cy={(R + 9) * Math.sin(a)}
                        r="3.5"
                        fill="#FFF8C7"
                      />
                    );
                  })}
                  {wedges.map((w) => {
                    const [top, bottom] = splitLabel(w.label);
                    const labelSize = labelFontSize(w.label, w.sweep);
                    const labelRadius = w.sweep < 12 ? 132 : 119;
                    return (
                      <g key={w.id || w.index}>
                        <path d={w.path} fill={w.color} stroke="#2a1609" strokeWidth="3" />
                        <g transform={`rotate(${w.center}) translate(0 -${labelRadius}) rotate(${-w.center})`}>
                          <text textAnchor="middle" fill="#fff" fontWeight="900" fontSize={labelSize}>{top}</text>
                          {bottom && <text y={labelSize + 2} textAnchor="middle" fill="#fff" fontWeight="800" fontSize={Math.max(7, labelSize - 2)}>{bottom}</text>}
                        </g>
                      </g>
                    );
                  })}
                  <circle r="61" fill="#2d1809" stroke="#f1bd54" strokeWidth="6" />
                  <circle r="45" fill="#072b28" stroke="#8d5c20" strokeWidth="3" />
                  <text y="-2" textAnchor="middle" fill="#ffe69a" fontSize="18" fontWeight="900">FORTUNE</text>
                  <text y="17" textAnchor="middle" fill="#90e5c8" fontSize="9" fontWeight="800">TREASURE HELM</text>
                </svg>
              </div>
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
                <svg width="38" height="46" viewBox="0 0 38 46">
                  <path d="M19 44 3 7A17 17 0 0 1 35 7Z" fill="#f1bd54" stroke="#5f3411" strokeWidth="3" />
                </svg>
              </div>
            </div>

            <button
              onClick={spin}
              disabled={spinning || !!today}
              className="vl-roulette-v3__spin w-full max-w-md py-4 rounded-2xl font-extrabold text-black disabled:opacity-50 active:scale-[0.99]"
              data-testid="roulette-spin"
            >
              {spinning ? 'Helm turning...' : today ? 'Treasure claimed today' : 'TURN THE FORTUNE HELM'}
            </button>

            {(result || today) && (
              <div className="mt-5 w-full max-w-md rounded-2xl p-4 text-center bg-white/5 border border-white/10" data-testid="roulette-result">
                <p className="text-xs uppercase tracking-widest text-[#F5D76E]">Result</p>
                <p className="text-2xl font-black mt-1">{result?.segment?.label || today?.label}</p>
                <p className="text-sm text-gray-400 mt-1">
                  {Number(result?.reward_fp ?? today?.reward_fp ?? 0) > 0
                    ? `${Number(result?.reward_fp ?? today?.reward_fp).toLocaleString()} FP credited`
                    : 'No FP credited this time'}
                </p>
              </div>
            )}

            <details className="mt-5 w-full max-w-md rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid="roulette-prize-list">
              <summary className="cursor-pointer select-none px-4 py-3 text-sm font-extrabold text-[#F5D76E]">
                View all roulette prizes
              </summary>
              <div className="px-4 pb-4 grid grid-cols-1 gap-2">
                {wedges.map((w) => (
                  <div key={`legend-${w.id || w.index}`} className="flex items-center justify-between rounded-xl bg-black/25 px-3 py-2 text-sm">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: w.color }} />
                      <span className="truncate font-bold">{w.label}</span>
                    </span>
                    <span className="text-gray-300 tabular-nums">{Number(w.chance || 0).toFixed(2)}%</span>
                  </div>
                ))}
              </div>
            </details>

            {result && !spinning && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/70 backdrop-blur-md"
                data-testid="roulette-result-modal"
                role="dialog"
                aria-modal="true"
              >
                <div className="relative w-full max-w-sm rounded-[28px] border border-[#F5D76E]/35 bg-[#14091E] p-6 text-center shadow-[0_22px_70px_rgba(0,0,0,0.65)]">
                  <button
                    type="button"
                    onClick={() => setResult(null)}
                    className="absolute right-4 top-4 w-9 h-9 rounded-full bg-white/10 border border-white/10 flex items-center justify-center"
                    aria-label="Close result"
                    data-testid="roulette-result-close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <div className="mx-auto w-16 h-16 rounded-2xl bg-[#F5D76E] text-black flex items-center justify-center shadow-[0_0_30px_rgba(245,215,110,0.35)]">
                    <Trophy className="w-9 h-9" />
                  </div>
                  <p className="mt-5 text-xs uppercase tracking-[0.3em] text-[#F5D76E] font-black">Roulette Result</p>
                  <h2 className="mt-2 text-3xl font-black">{result?.segment?.label || 'Daily Reward'}</h2>
                  <p className="mt-2 text-sm text-gray-300">
                    {Number(result?.reward_fp || 0) > 0
                      ? `${Number(result.reward_fp).toLocaleString()} FP has been credited to your account.`
                      : 'This prize does not credit FP automatically.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setResult(null)}
                    className="mt-6 w-full rounded-2xl py-3 font-black text-black active:scale-[0.99]"
                    style={{ background: 'linear-gradient(135deg,#D4AF37,#F5D76E)' }}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
